import React, { useContext, useEffect, useState } from 'react';
import { ErrorContext } from "../../components/GympinPagesProvider";
import { useSelector } from "react-redux";
import { transaction_increaseQuery } from "../../network/api/transactions.api";
import {
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Typography,
} from "@mui/material";
import { ReceiptLongRounded } from "@mui/icons-material";
import {
    increaseCorporateDeposit_completeRequestIncreaseCorporateDeposits,
    increaseCorporateDeposit_delete,
    increaseCorporateDeposit_getProFromaInvoice,
} from "../../network/api/increaseCorporateDeposit.api";
import _InvoicePayModal from "./partials/_InvoicePayModal";
import _InvoiceCancelModal from "./partials/_InvoiceCancelModal";
import _InvoiceCardItem from "./partials/_InvoiceCardItem";

const _ChargeInvoices = ({ refreshCode }) => {
    const error = useContext(ErrorContext);
    const corporate = useSelector(({ corporate }) => corporate.corporate);

    const [transactions, setTransactions] = useState(null);
    const [itemToPay, setItemToPay] = useState(null);
    const [itemToCancel, setItemToCancel] = useState(null);
    const [transactionReference, setTransactionReference] = useState("");
    const [downloadingId, setDownloadingId] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        getInvoiceList();
    }, [refreshCode]);

    function getInvoiceList() {
        if (!corporate?.Id) return;
        setTransactions(null);
        transaction_increaseQuery({
            queryType: "FILTER",
            CorporateId: corporate.Id,
            DepositStatus: "DRAFT",
            paging: { Page: 0, Size: 20, Desc: true }
        }).then((data) => {
            setTransactions(data.data.Data || {});
        }).catch(e => {
            setTransactions({ content: [] });
            try {
                error.showError({ message: e.response?.data?.Message || "خطا در دریافت لیست پیش‌فاکتورها" });
            } catch (f) {
                error.showError({ message: "خطای نامشخص" });
            }
        });
    }

    function getProFormaInvoice(item) {
        setDownloadingId(item.Id);
        increaseCorporateDeposit_getProFromaInvoice({
            Id: item.Id
        }).then((response) => {
            setDownloadingId(null);
            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `proforma-invoice-${item.Id}.pdf`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }).catch(e => {
            setDownloadingId(null);
            try {
                error.showError({ message: e.response?.data?.Message || "خطا در دانلود فایل پیش‌فاکتور" });
            } catch (f) {
                error.showError({ message: "خطای نامشخص" });
            }
        });
    }

    function getlabelOfRefrence() {
        if (!itemToPay) return "شناسه پرداخت";
        switch (itemToPay?.GatewayType) {
            case 'CARD_TRANSFER':
                return "کد پیگیری / ارجاع کارت به کارت";
            case 'BANK_TRANSFER':
                return "شماره پیگیری واریز به حساب / ساتنا";
            case 'CHEQUE':
                return "شماره صیادی / سریال چک";
            default:
                return "کد رهگیری پرداخت";
        }
    }

    function handleCompletePay(e) {
        e.preventDefault();
        if (!transactionReference) {
            error.showError({ message: `${getlabelOfRefrence()} الزامی است` });
            return;
        }

        setSubmitting(true);
        increaseCorporateDeposit_completeRequestIncreaseCorporateDeposits({
            Id: itemToPay.Id,
            TransactionReference: transactionReference,
        }).then(() => {
            setSubmitting(false);
            setItemToPay(null);
            setTransactionReference("");
            error.showError({ message: "اطلاعات پرداخت با موفقیت ثبت شد" });
            getInvoiceList();
        }).catch(e => {
            setSubmitting(false);
            try {
                error.showError({ message: e.response?.data?.Message || "خطا در ثبت پرداخت" });
            } catch (f) {
                error.showError({ message: "خطای نامشخص" });
            }
        });
    }

    function handleCancelInvoice(e) {
        e.preventDefault();
        if (!itemToCancel?.Id) return;

        setSubmitting(true);
        increaseCorporateDeposit_delete({
            Id: itemToCancel.Id
        }).then(() => {
            setSubmitting(false);
            setItemToCancel(null);
            error.showError({ message: "پیش‌فاکتور با موفقیت حذف شد" });
            getInvoiceList();
        }).catch(e => {
            setSubmitting(false);
            try {
                error.showError({ message: e.response?.data?.Message || "خطا در حذف پیش‌فاکتور" });
            } catch (f) {
                error.showError({ message: "خطای نامشخص" });
            }
        });
    }

    const isLoading = transactions === null;
    const invoiceList = transactions?.content || [];

    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                boxShadow: '0 2px 14px rgba(0, 0, 0, 0.03)',
                overflow: 'hidden'
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    p: 2.5,
                    px: 3,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.015)'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                    <ReceiptLongRounded sx={{ color: 'primary.main', fontSize: 22 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                        پیش‌فاکتورهای در انتظار
                    </Typography>
                </Box>
                <Chip
                    label={`${invoiceList.length} پیش‌فاکتور`}
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 700, borderRadius: 2 }}
                />
            </Box>

            <CardContent sx={{ p: 2.5 }}>
                {isLoading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
                        <CircularProgress size={32} thickness={4} sx={{ color: 'primary.main', mb: 1.5 }} />
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            در حال دریافت صورتحساب‌های معلق...
                        </Typography>
                    </Box>
                ) : invoiceList.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {invoiceList.map((row) => (
                            <_InvoiceCardItem
                                key={"tr-" + (row.Id || row.Serial)}
                                row={row}
                                isDownloading={downloadingId === row.Id}
                                onDownloadPdf={getProFormaInvoice}
                                onPay={setItemToPay}
                                onCancel={setItemToCancel}
                            />
                        ))}
                    </Box>
                ) : (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                        <Box
                            sx={{
                                width: 58,
                                height: 58,
                                borderRadius: '50%',
                                bgcolor: 'rgba(28, 139, 156, 0.08)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 1.5,
                                color: 'primary.main'
                            }}
                        >
                            <ReceiptLongRounded sx={{ fontSize: 28 }} />
                        </Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
                            پیش‌فاکتور معلقی وجود ندارد
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', maxWidth: 280, display: 'block', mx: 'auto' }}>
                            کلیه درخواست‌های افزایش شارژ شما تسویه شده یا پیش‌فاکتور ثبت‌نشده‌ای وجود ندارد.
                        </Typography>
                    </Box>
                )}
            </CardContent>

            <_InvoiceCancelModal
                itemToCancel={itemToCancel}
                onClose={() => setItemToCancel(null)}
                submitting={submitting}
                handleCancelInvoice={handleCancelInvoice}
            />

            <_InvoicePayModal
                itemToPay={itemToPay}
                onClose={() => setItemToPay(null)}
                submitting={submitting}
                handleCompletePay={handleCompletePay}
                getlabelOfRefrence={getlabelOfRefrence}
                transactionReference={transactionReference}
                setTransactionReference={setTransactionReference}
            />
        </Card>
    );
};

export default _ChargeInvoices;
